import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { CODE_LENGTH, clearCodeAt, fillCode } from '../forgotPassword.helpers';

/**
 * Six one-digit boxes for the verification code. Typing advances, Backspace goes back, the
 * arrows move, and pasting (or the browser's one-time-code autofill) fills every box.
 * The numeric keyboard opens on phones. `ref.current.focus()` focuses the first empty box.
 */
const OtpInput = forwardRef(function OtpInput({ value = '', onChange, onComplete, disabled, invalid, labelledBy, describedBy }, ref) {
    const inputs = useRef([]);

    const focusBox = (index) => {
        const box = inputs.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))];
        if (box) {
            box.focus();
            box.select();
        }
    };

    useImperativeHandle(ref, () => ({ focus: () => focusBox(Math.min(value.length, CODE_LENGTH - 1)) }), [value.length]);

    // Box to focus once the new value is rendered (see the layout effect below).
    const pendingFocus = useRef(null);

    useLayoutEffect(() => {
        if (pendingFocus.current === null) return;
        const index = pendingFocus.current;
        pendingFocus.current = null;
        focusBox(index);
    });

    const apply = ({ code, focus }, completedByPaste) => {
        if (code === value) {
            focusBox(focus);
        } else {
            pendingFocus.current = focus;
            onChange?.(code);
        }
        if (completedByPaste && code.length === CODE_LENGTH) onComplete?.(code);
    };

    const handleChange = (index, e) => {
        const text = e.target.value;
        // Soft keyboards that send no Backspace keydown just empty the box.
        if (!text) {
            apply(clearCodeAt(value, index));
            return;
        }
        // The box already held a digit: the new one is the character that isn't it.
        const typed = text.length === 2 && value[index] ? text.replace(value[index], '') || text[1] : text;
        const result = fillCode(value, index, typed);
        apply(result, typed.replace(/\D/g, '').length >= CODE_LENGTH);
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            apply(clearCodeAt(value, index));
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            focusBox(index - 1);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            focusBox(Math.min(index + 1, value.length));
        }
    };

    const handlePaste = (index, e) => {
        const text = e.clipboardData?.getData('text') || '';
        if (!/\d/.test(text)) return;
        e.preventDefault();
        apply(fillCode(value, index, text), text.replace(/\D/g, '').length >= CODE_LENGTH);
    };

    // Boxes are filled in order: focusing one past the first empty box moves to that box.
    const handleFocus = (index, e) => {
        if (index > value.length) focusBox(value.length);
        else e.target.select();
    };

    return (
        <div role="group" aria-labelledby={labelledBy} aria-describedby={describedBy} className="grid grid-cols-6 gap-2 sm:gap-2.5">
            {Array.from({ length: CODE_LENGTH }, (_, index) => {
                const digit = value[index] || '';
                return (
                    <input
                        key={index}
                        ref={(el) => {
                            inputs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        // Browsers offer the code from the email/SMS only on the first box.
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        // Room for an autofilled code on top of a digit already there.
                        maxLength={CODE_LENGTH + 1}
                        aria-label={`Dígito ${index + 1} de ${CODE_LENGTH}`}
                        aria-invalid={invalid || undefined}
                        disabled={disabled}
                        value={digit}
                        onChange={(e) => handleChange(index, e)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={(e) => handlePaste(index, e)}
                        onFocus={(e) => handleFocus(index, e)}
                        className={[
                            'h-12 w-full min-w-0 rounded-lg border bg-white p-0 text-center text-xl font-semibold tabular-nums text-surface-900 caret-primary-600 outline-none transition-[border-color,box-shadow] sm:h-14',
                            'focus:border-primary-500 focus:shadow-focus disabled:cursor-not-allowed disabled:bg-surface-50 disabled:text-surface-400',
                            invalid ? 'border-danger-solid' : digit ? 'border-surface-300' : 'border-surface-200 hover:border-surface-300'
                        ].join(' ')}
                    />
                );
            })}
        </div>
    );
});

export default OtpInput;
